import ApiClient from '../ApiClient';
import EmailService from './EmailService';

const EMPTY_EMAIL_AUTH_PAYLOAD = { emailType: '', fromEmail: '', accessToken: '' };

// Every meeting/calendar provider (Zoom, Google Meet) resolves its token server-side
// now — there's no remaining client-token-based provider to include here.
const buildMeetingProvidersPayload = async () => {
  return [];
};

// Resolving the invite-email auth (e.g. an expired Gmail token) must never block
// saving/deleting the calendar event itself — only the invite email gets skipped.
const getEmailAuthPayload = async () => {
  try {
    return await EmailService.ensureCalendarEmailAuthPayload();
  } catch {
    return EMPTY_EMAIL_AUTH_PAYLOAD;
  }
};

export async function buildAddCalendarEventPayload({
  title,
  startTime,
  durationInMinutes = 30,
  guestEmails = [],
  notes = null,
  isVCEnabled = false,
  meetingProvider = null,
  meetingUrl = null,
  meetingId = null,
  recurrenceType = null,
}) {
  return {
    title: (title || '').trim(),
    startTime: new Date(startTime).toISOString(),
    durationInMinutes: Number(durationInMinutes) || 30,
    guestEmails,
    notes: notes || null,
    isVCEnabled: Boolean(isVCEnabled),
    meetingProvider: isVCEnabled ? meetingProvider : null,
    meetingUrl: isVCEnabled ? meetingUrl : null,
    externalMeetingId: isVCEnabled ? (meetingId ?? null) : null,
    recurrenceType: recurrenceType && recurrenceType !== 'none' ? recurrenceType : null,
    emailAuth: await getEmailAuthPayload(),
  };
}

export async function buildUpdateCalendarEventPayload({
  id,
  title,
  startTime,
  durationInMinutes = 30,
  guestEmails = [],
  notes = null,
  isVCEnabled = false,
  meetingProvider = null,
  meetingUrl = null,
  meetingId = null,
  sendEmail = false,
  recurrenceType = null,
}) {
  return {
    id,
    title: (title || '').trim(),
    startTime: new Date(startTime).toISOString(),
    durationInMinutes: Number(durationInMinutes) || 30,
    guestEmails,
    notes: notes || null,
    isVCEnabled: Boolean(isVCEnabled),
    meetingProvider: isVCEnabled ? meetingProvider : null,
    meetingUrl: isVCEnabled ? meetingUrl : null,
    externalMeetingId: isVCEnabled ? (meetingId ?? null) : null,
    sendEmail: Boolean(sendEmail),
    recurrenceType: recurrenceType && recurrenceType !== 'none' ? recurrenceType : null,
    emailAuth: await getEmailAuthPayload(),
  };
}

const CalendarService = {
  async addCalendarEvent(payload) {
    return ApiClient.post('/Calendar/add-calendar-event', payload);
  },

  async updateCalendarEvent(payload) {
    return ApiClient.put('/Calendar/update-calendar-event', payload);
  },

  async getCalendarEvents() {
    const providerTokens = await buildMeetingProvidersPayload();
    return ApiClient.post('/Calendar/calendar-list', { providerTokens });
  },

  async deleteCalendarEvent(id) {
    return ApiClient.delete(`/Calendar/delete-calendar-event/${id}`, {
      data: await getEmailAuthPayload(),
    });
  },
};

export default CalendarService;
